/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { makeObservable, observable, action, runInAction } from 'mobx';
import { Button, Dimmer, Header, List, Loader, Segment } from 'semantic-ui-react';
import _ from 'lodash';

import { getAddAwsAccountFormFields } from '../../models/forms/AddAwsAccountForm';
import { displayError } from '../../helpers/notification';
import { withRouter } from '../../helpers/routing';
import validate from '../../models/forms/Validate';

class AddAwsAccount extends React.Component {
  formProcessing = false;
  validationErrors = new Map();

  constructor(props) {
    super(props);
    makeObservable(this, {
      formProcessing: observable,
      validationErrors: observable,
    });

    this.state = {
      // eslint-disable-next-line react/no-unused-state
      role: 'guest',
      // eslint-disable-next-line react/no-unused-state
      status: 'active',
    };

    runInAction(() => {
      this.awsAccount = {};
    });
    // this.form = getAddAwsAccountForm();
    this.addAwsAccountFormFields = getAddAwsAccountFormFields();
  }

  render() {
    return (
      <div className="mt2 animated fadeIn">
        <Header as="h2" icon textAlign="center" className="mt3" color="grey">
          Add AWS Account
        </Header>
        <div className="mt3 ml3 mr3 animated fadeIn">{this.renderAddAwsAccountForm()}</div>
      </div>
    );
  }

  // eslint-disable-next-line react/no-unused-state
  // handleRoleChange = (e, { value }) => this.setState({ role: value });

  // eslint-disable-next-line react/no-unused-state
  // handleStatusChange = (e, { value }) => this.setState({ status: value });

  renderAddAwsAccountForm() {
    const processing = this.formProcessing;
    const fields = this.addAwsAccountFormFields;
    const toEditableInput = (attributeName, type = 'text') => {
      const handleChange = action(event => {
        event.preventDefault();
        this.awsAccount[attributeName] = event.target.value;
      });
      return (
        <div className="ui focus input">
          <input
            type={type}
            defaultValue={this.awsAccount[attributeName]}
            placeholder={fields[attributeName].placeholder || ''}
            onChange={handleChange}
          />
        </div>
      );
    };

    return (
      <Segment basic className="ui fluid form">
        <Dimmer active={processing} inverted>
          <Loader inverted>Checking</Loader>
        </Dimmer>
        {Object.keys(fields).map(field => (
          <React.Fragment key={field}>
            {this.renderField(field, toEditableInput(field))}
            <div className="mb4" />
          </React.Fragment>
        ))}
        {this.renderButtons()}
      </Segment>
    );
  }

  renderButtons() {
    const processing = this.formProcessing;
    return (
      <div className="mt3">
        <Button floated="right" color="blue" icon disabled={processing} className="ml2" onClick={this.handleSubmit}>
          Add AWS Account
        </Button>
        <Button floated="right" disabled={processing} onClick={this.handleCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  renderField(name, component) {
    const fields = this.addAwsAccountFormFields;
    const explain = fields[name].explain;
    const label = fields[name].label;
    const hasExplain = !_.isEmpty(explain);
    const fieldErrors = this.validationErrors.get(name);
    const hasError = !_.isEmpty(fieldErrors);

    return (
      <div>
        <Header className="mr3 mt0" as="h2" color="grey">
          {label}
        </Header>
        {hasExplain && <div className="mb2">{explain}</div>}
        <div className={`ui big field input block m0 ${hasError ? 'error' : ''}`}>{component}</div>
        {hasError && (
          <div className="ui pointing red basic label">
            <List>
              {_.map(fieldErrors, fieldError => (
                <List.Item key={name}>
                  <List.Content>{fieldError}</List.Content>
                </List.Item>
              ))}
            </List>
          </div>
        )}
      </div>
    );
  }

  goto(pathname) {
    // const location = this.props.location;
    // const link = createLink({ location, pathname });
    this.props.navigate(pathname);
  }

  handleCancel = action(event => {
    event.preventDefault();
    event.stopPropagation();
    this.formProcessing = false;
    this.goto('/accounts');
  });

  handleSubmit = action(async () => {
    this.formProcessing = true;
    try {
      // Perform client side validations first
      const validationResult = await validate(this.awsAccount, this.addAwsAccountFormFields);
      // if there are any client side validation errors then do not attempt to make API call
      if (validationResult.fails()) {
        runInAction(() => {
          this.validationErrors = validationResult.errors;
          this.formProcessing = false;
        });
      } else {
        // There are no client side validation errors so ask the store to add user (which will make API call to server to add the user)

        await this.props.awsAccountsStore.addAwsAccount(this.awsAccount);
        runInAction(() => {
          this.formProcessing = false;
        });
        this.goto('/accounts');
      }
    } catch (error) {
      runInAction(() => {
        this.formProcessing = false;
      });
      displayError(error);
    }
  });

  /* getStore() {
    return this.props.usersStore;
  } */
}

export default inject('usersStore', 'awsAccountsStore')(withRouter(observer(AddAwsAccount)));
